import * as THREE from 'three';
import type { LevelData } from '@/world/LevelLoader';
import { CANAL_DEPTH, HARBOR_LEVEL, QUAY_Z, cityGround } from '@/world/districts/harbor';
import { WORKERS_LEVEL } from '@/world/districts/workers';
import { CITY } from '@/world/grid';
import { levelDataFromBlueprint, type DistrictBlueprint } from '@/world/DistrictBlueprint';

const CONNECTOR_X = 116;
const CONNECTOR_WIDTH = 104;
const QUAY_WALK_Z = QUAY_Z - 2.5;
/** Apron tussen de kadeloodsen en het kadedek, waar de servicepockets staan. */
const SERVICE_Z = 13;

export interface CityBounds {
  readonly x: number;
  readonly z: number;
  readonly halfX: number;
  readonly halfZ: number;
}

export interface CityChunk {
  readonly id: string;
  readonly district: string;
  readonly bounds: CityBounds;
  readonly levelData: LevelData;
  readonly neighbors: readonly string[];
}

const CONNECTOR_BUILDINGS: LevelData['buildings'] = [
  { kind: 'warehouse', x: 72, z: -16, width: 10, depth: 12, floors: 3, facing: 'south', wall: 'nightBrick', seed: 'connector-warehouse-1', batch: 'connector-north' },
  { kind: 'warehouse', x: 83, z: -16.5, width: 10, depth: 13, floors: 2, facing: 'south', wall: 'slateGreen', seed: 'connector-warehouse-2', batch: 'connector-north' },
  { kind: 'tenement', x: 96, z: -15.5, width: 12, depth: 11, floors: 5, facing: 'south', wall: 'oxideMaroon', seed: 'connector-tenement-1', batch: 'connector-north' },
  { kind: 'tenement', x: 108, z: -15.5, width: 10, depth: 11, floors: 4, facing: 'south', wall: 'brickRed', seed: 'connector-tenement-2', batch: 'connector-north' },
  { kind: 'canalHouse', x: 116, z: -14, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'nightBrick', seed: 'connector-canal-1', batch: 'connector-north' },
  { kind: 'canalHouse', x: 122, z: -14, width: 4, depth: 8, floors: 4, facing: 'south', wall: 'slateGreen', seed: 'connector-canal-2', batch: 'connector-north' },
  { kind: 'tenement', x: 132, z: -15.5, width: 10, depth: 11, floors: 4, facing: 'south', wall: 'oxideMaroon', seed: 'connector-tenement-3', batch: 'connector-north' },
  { kind: 'warehouse', x: 145, z: -16.5, width: 10, depth: 13, floors: 3, facing: 'south', wall: 'nightBrick', seed: 'connector-warehouse-3', batch: 'connector-north' },
  { kind: 'warehouse', x: 156, z: -16, width: 10, depth: 12, floors: 2, facing: 'south', wall: 'brickRed', seed: 'connector-warehouse-4', batch: 'connector-north' },
  { kind: 'warehouse', x: 76, z: 7.1, width: 10, depth: 4, floors: 1, facing: 'north', wall: 'nightBrick', seed: 'connector-quay-1', batch: 'connector-south' },
  { kind: 'warehouse', x: 96, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'brickRed', seed: 'connector-quay-2a', batch: 'connector-south' },
  { kind: 'warehouse', x: 118, z: 7.1, width: 10, depth: 4, floors: 1, facing: 'north', wall: 'slateGreen', seed: 'connector-quay-2', batch: 'connector-south' },
  { kind: 'warehouse', x: 137, z: 7.1, width: 8, depth: 4, floors: 1, facing: 'north', wall: 'nightBrick', seed: 'connector-quay-2b', batch: 'connector-south' },
  { kind: 'warehouse', x: 154, z: 7.1, width: 10, depth: 4, floors: 1, facing: 'north', wall: 'oxideMaroon', seed: 'connector-quay-3', batch: 'connector-south' },
  { kind: 'warehouse', x: 72, z: -26.5, width: 10, depth: 6, floors: 3, facing: 'south', wall: 'nightBrick', seed: 'connector-back-1', batch: 'connector-back' },
  { kind: 'warehouse', x: 88, z: -26.5, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'brickRed', seed: 'connector-back-2', batch: 'connector-back' },
  { kind: 'warehouse', x: 106, z: -26.5, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'slateGreen', seed: 'connector-back-3', batch: 'connector-back' },
  { kind: 'warehouse', x: 126, z: -26.5, width: 12, depth: 6, floors: 2, facing: 'south', wall: 'nightBrick', seed: 'connector-back-4', batch: 'connector-back' },
  { kind: 'warehouse', x: 146, z: -26.5, width: 12, depth: 6, floors: 3, facing: 'south', wall: 'oxideMaroon', seed: 'connector-back-5', batch: 'connector-back' },
  { kind: 'warehouse', x: 160, z: -26.5, width: 8, depth: 6, floors: 2, facing: 'south', wall: 'brickRed', seed: 'connector-back-6', batch: 'connector-back' },
];

export const CONNECTOR_BLUEPRINT: DistrictBlueprint = {
  id: 'connector',
  levelName: 'NA_Chunk_HarborWorkersConnector',
  districtName: 'Connector Docks',
  primaryRoute: 'Fast east-west connector avenue between Harbor and Workers.',
  landmarks: ['Bridge and quay service pocket.'],
  ground: cityGround(CONNECTOR_WIDTH, CONNECTOR_X),
  quayWall: {
    length: CONNECTOR_WIDTH,
    x: CONNECTOR_X,
    z: QUAY_Z,
  },
  quayDeck: {
    length: CONNECTOR_WIDTH,
    depth: CITY.quayDepth,
    x: CONNECTOR_X,
    z: QUAY_Z - CITY.quayDepth / 2,
  },
  water: {
    width: CONNECTOR_WIDTH,
    depth: CANAL_DEPTH,
    x: CONNECTOR_X,
    z: QUAY_Z + CANAL_DEPTH / 2,
  },
  obstacles: [{ x: CONNECTOR_X, z: QUAY_Z + 1, halfX: CONNECTOR_WIDTH / 2, halfZ: 1, height: 2 }],
  // Zie HARBOR_BLUEPRINT: het kanaal is de zuidgrens, dus geen brug naar het niets.
  bridges: [],
  roads: [
    {
      id: 'connector-eastbound',
      x: 116,
      z: 2.5,
      halfX: 51,
      halfZ: 1.35,
      intent: 'Fast eastbound connector lane.',
      route: {
        id: 'connector-eastbound',
        kind: 'sedan',
        points: [new THREE.Vector2(66, 2.5), new THREE.Vector2(92, 2.5), new THREE.Vector2(120, 2.5), new THREE.Vector2(146, 2.5), new THREE.Vector2(166, 2.5)],
        speedLimit: 4.6,
        loop: true,
      },
    },
    {
      id: 'connector-westbound',
      x: 116,
      z: -2.8,
      halfX: 51,
      halfZ: 1.35,
      intent: 'Fast westbound connector lane.',
      route: {
        id: 'connector-westbound',
        kind: 'van',
        points: [new THREE.Vector2(166, -2.8), new THREE.Vector2(140, -2.8), new THREE.Vector2(112, -2.8), new THREE.Vector2(86, -2.8), new THREE.Vector2(66, -2.8)],
        speedLimit: 3.8,
        loop: true,
      },
    },
  ],
  pedestrianZones: [
    { id: 'connector-north-sidewalk', kind: 'sidewalk', x: 116, z: -5.3, halfX: 51, halfZ: 1.0, intent: 'North sidewalk for lamps, hydrants and phone booths.', allowedProps: ['hydrant', 'phoneBooth'] },
    { id: 'connector-corners', kind: 'intersection', x: 66, z: -5.3, halfX: 2.0, halfZ: 0.8, intent: 'Harbor-side intersection furniture and traffic light.', allowedProps: ['trafficLight'] },
    { id: 'connector-east-corners', kind: 'intersection', x: 166, z: 5.0, halfX: 2.0, halfZ: 1.2, intent: 'Workers-side intersection furniture and traffic light.', allowedProps: ['trafficLight'] },
    { id: 'connector-quay-strip', kind: 'quay', x: CONNECTOR_X, z: QUAY_WALK_Z, halfX: 50, halfZ: 2.0, intent: 'Quay edge for benches and bollards.', allowedProps: ['bench', 'bollard'] },
    { id: 'connector-service-pocket', kind: 'serviceYard', x: 88, z: SERVICE_Z, halfX: 18, halfZ: 1.5, intent: 'Kade-opslag and service clutter outside the traffic corridor.', allowedProps: ['crateStack'] },
    { id: 'connector-east-service-pocket', kind: 'serviceYard', x: 146, z: SERVICE_Z, halfX: 17, halfZ: 1.5, intent: 'Second dock pocket for cargo piles.', allowedProps: ['crateStack'] },
    { id: 'connector-crane-yard', kind: 'serviceYard', x: CONNECTOR_X, z: QUAY_Z - 4, halfX: 4.0, halfZ: 1.4, intent: 'Crane pad on the industrial quay.', allowedProps: ['crane'] },
    { id: 'connector-parking-north', kind: 'parkingBay', x: CONNECTOR_X, z: -4.1, halfX: 48, halfZ: 0.95, intent: 'Curbside parking bay along the north sidewalk.', allowedVehicles: ['sedan', 'van', 'wreck'] },
    { id: 'connector-water', kind: 'water', x: CONNECTOR_X, z: QUAY_Z + CANAL_DEPTH / 2, halfX: 50, halfZ: CANAL_DEPTH / 2 - 1, intent: 'Canal water continuing through the connector.', allowedProps: ['boat'] },
    { id: 'connector-debug-objective', kind: 'objectiveArea', x: 124, z: -5.3, halfX: 2, halfZ: 1, intent: 'Temporary connector objective marker placeholder on the north sidewalk until interiors split out.' },
  ],
  buildingBlocks: [
    {
      id: 'connector-north-industrial-row',
      intent: 'North row blends warehouses and a few dense residential buildings along the through-road.',
      buildings: CONNECTOR_BUILDINGS.filter((placement) => placement.batch === 'connector-north'),
    },
    {
      id: 'connector-back-industrial-wall',
      intent: 'A setback industrial wall gives skyline density while staying behind the playable sidewalk and traffic lanes.',
      buildings: CONNECTOR_BUILDINGS.filter((placement) => placement.batch === 'connector-back'),
    },
    {
      id: 'connector-south-quay-row',
      intent: 'South quay warehouses frame service pockets while keeping the drive corridor clear.',
      buildings: CONNECTOR_BUILDINGS.filter((placement) => placement.batch === 'connector-south'),
    },
  ],
  streetlamps: [
    { x: 70, z: -5.5, rotation: 0 },
    { x: 86, z: -5.5, rotation: 0 },
    { x: 102, z: -5.5, rotation: 0 },
    { x: 118, z: -5.5, rotation: 0 },
    { x: 134, z: -5.5, rotation: 0 },
    { x: 150, z: -5.5, rotation: 0 },
    { x: 166, z: -5.5, rotation: 0 },
    { x: 70, z: QUAY_WALK_Z, rotation: Math.PI },
    { x: 94, z: QUAY_WALK_Z, rotation: Math.PI },
    { x: 118, z: QUAY_WALK_Z, rotation: Math.PI },
    { x: 142, z: QUAY_WALK_Z, rotation: Math.PI },
    { x: 162, z: QUAY_WALK_Z, rotation: Math.PI },
  ],
  propClusters: [
    {
      id: 'connector-intersection-control',
      zoneId: 'connector-corners',
      intent: 'Traffic control is anchored to the entry intersection, not the middle of a lane.',
      props: [
        { kind: 'trafficLight', x: 66, z: -5.3, rotation: Math.PI / 2 },
        { kind: 'trafficLight', x: 166, z: 5.0, rotation: -Math.PI / 2 },
      ],
    },
    {
      id: 'connector-sidewalk-fixtures',
      zoneId: 'connector-north-sidewalk',
      intent: 'Small fixtures sit on the pedestrian edge with clear vehicle clearance.',
      props: [
        { kind: 'hydrant', x: 100, z: -4.8, rotation: 0 },
        { kind: 'phoneBooth', x: 154, z: -5.0, rotation: Math.PI },
      ],
    },
    {
      id: 'connector-quay-furniture',
      zoneId: 'connector-quay-strip',
      intent: 'Bollards and a bench draw a readable dock edge.',
      props: [
        { kind: 'bench', x: 72, z: QUAY_WALK_Z, rotation: Math.PI },
        { kind: 'bench', x: 134, z: QUAY_WALK_Z, rotation: Math.PI },
        { kind: 'bollard', x: 70, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 86, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 102, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 118, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 134, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 150, z: QUAY_Z - 0.8 },
        { kind: 'bollard', x: 164, z: QUAY_Z - 0.8 },
      ],
    },
    {
      id: 'connector-service-cover',
      zoneId: 'connector-service-pocket',
      intent: 'A small crate cluster sits in a service pocket, not inside the warehouse or lane.',
      props: [
        { kind: 'crateStack', x: 76, z: SERVICE_Z, rotation: 0.2 },
        { kind: 'crateStack', x: 88, z: SERVICE_Z - 0.3, rotation: -0.25 },
        { kind: 'crateStack', x: 100, z: SERVICE_Z + 0.2, rotation: 0.35 },
      ],
    },
    {
      id: 'connector-east-service-cover',
      zoneId: 'connector-east-service-pocket',
      intent: 'Cargo clutter thickens the east service edge without entering the drive lane.',
      props: [
        { kind: 'crateStack', x: 132, z: SERVICE_Z - 0.2, rotation: 0.25 },
        { kind: 'crateStack', x: 146, z: SERVICE_Z, rotation: -0.15 },
        { kind: 'crateStack', x: 160, z: SERVICE_Z - 0.2, rotation: -0.3 },
      ],
    },
    {
      id: 'connector-crane-landmark',
      zoneId: 'connector-crane-yard',
      intent: 'The crane is authored on a quay pad with clear building clearance.',
      props: [{ kind: 'crane', x: CONNECTOR_X, z: QUAY_Z - 4, rotation: -Math.PI / 2 }],
    },
    {
      id: 'connector-moored-boats',
      zoneId: 'connector-water',
      intent: 'Small workboats tie the connector visually to the canal reference boards.',
      props: [
        { kind: 'boat', x: 84, z: QUAY_Z + 7.4, rotation: Math.PI / 2 },
        { kind: 'boat', x: 148, z: QUAY_Z + 7.4, rotation: Math.PI / 2 },
      ],
    },
  ],
  parkedVehicles: [
    { id: 'connector-parked-sedan-1', kind: 'sedan', position: new THREE.Vector2(92, -4.1), rotation: Math.PI / 2, stealable: true },
    { id: 'connector-parked-van-1', kind: 'van', position: new THREE.Vector2(138, -4.1), rotation: Math.PI / 2, stealable: false },
  ],
  safehouse: { x: 124, z: -5.3, radius: 1 },
};

const CONNECTOR_LEVEL: LevelData = levelDataFromBlueprint(CONNECTOR_BLUEPRINT);

export const CITY_CHUNKS: readonly CityChunk[] = [
  {
    id: 'harbor-west',
    district: 'harbor',
    bounds: boundsFromLevel(HARBOR_LEVEL),
    levelData: HARBOR_LEVEL,
    neighbors: ['harbor-workers-connector'],
  },
  {
    id: 'harbor-workers-connector',
    district: 'industrialDocks',
    bounds: boundsFromLevel(CONNECTOR_LEVEL),
    levelData: CONNECTOR_LEVEL,
    neighbors: ['harbor-west', 'workers-east'],
  },
  {
    id: 'workers-east',
    district: 'workers',
    bounds: boundsFromLevel(WORKERS_LEVEL),
    levelData: WORKERS_LEVEL,
    neighbors: ['harbor-workers-connector'],
  },
] as const;

export function chunkAt(position: THREE.Vector2, chunks: readonly CityChunk[] = CITY_CHUNKS): CityChunk | null {
  return chunks.find((chunk) => contains(chunk.bounds, position.x, position.y)) ?? null;
}

export function boundsFromLevel(level: LevelData): CityBounds {
  return {
    x: level.ground.x,
    z: level.ground.z,
    halfX: level.ground.width / 2,
    halfZ: level.ground.depth / 2,
  };
}

export function contains(bounds: CityBounds, x: number, z: number): boolean {
  return Math.abs(x - bounds.x) <= bounds.halfX && Math.abs(z - bounds.z) <= bounds.halfZ;
}
